import React, { useEffect, useState } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const VDModsEditor = () => {
  const LANGS = ["En", "Ar", "Ku"];

  const [form, setForm] = useState({
    // TOP
    TopTitleEn: "", TopTitleAr: "", TopTitleKu: "",
    TopDescEn: "", TopDescAr: "", TopDescKu: "",

    // CENTER
    CenterTitleEn: "", CenterTitleAr: "", CenterTitleKu: "",
    CenterDescEn: "", CenterDescAr: "", CenterDescKu: "",

    // CARDS
    Cards: [
      { CardId: 15260, TitleEn: "", TitleAr: "", TitleKu: "", DescEn: "", DescAr: "", DescKu: "" },
      { CardId: 15261, TitleEn: "", TitleAr: "", TitleKu: "", DescEn: "", DescAr: "", DescKu: "" },
      { CardId: 15262, TitleEn: "", TitleAr: "", TitleKu: "", DescEn: "", DescAr: "", DescKu: "" },
      { CardId: 15263, TitleEn: "", TitleAr: "", TitleKu: "", DescEn: "", DescAr: "", DescKu: "" }
    ]
  });

  const [images, setImages] = useState({
    main: null,
    cards: {}
  });

  const [loading, setLoading] = useState(true);

  // MODAL
  const [showModal, setShowModal] = useState(false);
  const [uploadField, setUploadField] = useState(null); // "main" or cardId
  const [uploadFile, setUploadFile] = useState(null);

  const resolveUrl = (url) =>
    url?.startsWith("http") ? url : `${API_BASE}${url}`;

  // ================================
  // LOAD DATA (EN/AR/KU)
  // ================================
  useEffect(() => {
    const load = async () => {
      try {
        const [en, ar, ku] = await Promise.all([
          axios.get(`${API_BASE}/api/VDModsSection?lang=en`),
          axios.get(`${API_BASE}/api/VDModsSection?lang=ar`),
          axios.get(`${API_BASE}/api/VDModsSection?lang=ku`),
        ]);

        // FILL SECTION
        setForm((prev) => ({
          ...prev,
          TopTitleEn: en.data.topTitle,
          TopTitleAr: ar.data.topTitle,
          TopTitleKu: ku.data.topTitle,

          TopDescEn: en.data.topDesc,
          TopDescAr: ar.data.topDesc,
          TopDescKu: ku.data.topDesc,

          CenterTitleEn: en.data.centerTitle,
          CenterTitleAr: ar.data.centerTitle,
          CenterTitleKu: ku.data.centerTitle,

          CenterDescEn: en.data.centerDesc,
          CenterDescAr: ar.data.centerDesc,
          CenterDescKu: ku.data.centerDesc,

          // CARDS
          Cards: en.data.cards.map((c, i) => ({
            CardId: c.id,
            TitleEn: c.title,
            TitleAr: ar.data.cards[i].title,
            TitleKu: ku.data.cards[i].title,
            DescEn: c.desc,
            DescAr: ar.data.cards[i].desc,
            DescKu: ku.data.cards[i].desc,
          }))
        }));

        // IMAGES
        setImages({
          main: en.data.mainImage,
          cards: en.data.cards.reduce((acc, c) => {
            acc[c.id] = c.image;
            return acc;
          }, {})
        });

        setLoading(false);
      } catch (err) {
        console.error("Failed to load VDMods:", err);
      }
    };

    load();
  }, []);

  const handleChange = (e, idx, field) => {
    if (idx === undefined) {
      setForm({ ...form, [e.target.name]: e.target.value });
    } else {
      const updated = [...form.Cards];
      updated[idx][field] = e.target.value;
      setForm({ ...form, Cards: updated });
    }
  };

  // ================================
  // SAVE TEXT
  // ================================
  const handleSave = async () => {
    const token = localStorage.getItem("token");

    try {
      await axios.put(`${API_BASE}/api/VDModsSection`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });

      alert("Saved successfully!");
    } catch (err) {
      console.error("SAVE FAILED", err);
      alert("Failed to save.");
    }
  };

  // ================================
  // UPLOAD IMAGE
  // ================================
  const handleUpload = async () => {
    if (!uploadFile) return alert("Select a file first.");

    const token = localStorage.getItem("token");
    const fd = new FormData();

    if (uploadField === "main") {
      fd.append("MainImage", uploadFile);
    } else {
      fd.append("CardId", uploadField);
      fd.append("CardImage", uploadFile);
    }

    try {
      await axios.post(`${API_BASE}/api/VDModsSection/media`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      alert("Image uploaded!");
      setShowModal(false);
      window.location.reload();
    } catch (err) {
      console.error("UPLOAD FAILED", err);
      alert("Upload failed.");
    }
  };

  if (loading) return <p>Loading...</p>;

  // ================================
  // RENDER
  // ================================
  return (
    <div className="container py-4">
      <h2 className="fw-bold mb-4">VOYAH DREAM – Mods Section Editor</h2>

      {/* ============================
          TEXT INPUTS (3 LANGUAGES)
      ============================ */}
      <div className="row">
        {LANGS.map((lang) => (
          <div key={lang} className="col-lg-4 col-md-6 mb-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body">
                <h5 className="text-primary">{lang}</h5>

                {/* TOP TITLE */}
                <label className="form-label mt-3">Top Title</label>
                <input
                  className="form-control"
                  name={`TopTitle${lang}`}
                  value={form[`TopTitle${lang}`]}
                  onChange={handleChange}
                />

                {/* TOP DESC */}
                <label className="form-label mt-2">Top Description</label>
                <textarea
                  className="form-control"
                  rows={3}
                  name={`TopDesc${lang}`}
                  value={form[`TopDesc${lang}`]}
                  onChange={handleChange}
                />

                {/* CENTER TITLE */}
                <label className="form-label mt-3">Center Title</label>
                <input
                  className="form-control"
                  name={`CenterTitle${lang}`}
                  value={form[`CenterTitle${lang}`]}
                  onChange={handleChange}
                />

                {/* CENTER DESC */}
                <label className="form-label mt-2">Center Description</label>
                <textarea
                  className="form-control"
                  rows={3}
                  name={`CenterDesc${lang}`}
                  value={form[`CenterDesc${lang}`]}
                  onChange={handleChange}
                />

                {/* CARDS */}
                <h6 className="fw-bold mt-4">Cards</h6>

                {form.Cards.map((card, idx) => (
                  <div key={card.CardId} className="mb-3">
                    <label className="form-label">
                      Card {idx + 1} Title
                    </label>
                    <input
                      className="form-control"
                      value={card[`Title${lang}`]}
                      onChange={(e) =>
                        handleChange(e, idx, `Title${lang}`)
                      }
                    />

                    <label className="form-label mt-2">
                      Card {idx + 1} Description
                    </label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={card[`Desc${lang}`]}
                      onChange={(e) =>
                        handleChange(e, idx, `Desc${lang}`)
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* SAVE BUTTON */}
      <div className="text-end mb-4">
        <button className="btn btn-primary px-4" onClick={handleSave}>
          Save Changes
        </button>
      </div>

      {/* ============================
          MAIN IMAGE EDIT
      ============================ */}
      <h4 className="mb-3">Main Image</h4>
      <div className="card shadow-sm border-0 mb-5">
        <div className="card-body">
          {images.main ? (
            <img
              src={resolveUrl(images.main)}
              className="rounded w-100 mb-3"
              style={{ maxHeight: "280px", objectFit: "cover" }}
              alt=""
            />
          ) : (
            <p>No image uploaded.</p>
          )}

          <button
            className="btn btn-primary"
            onClick={() => {
              setUploadField("main");
              setUploadFile(null);
              setShowModal(true);
            }}
          >
            Upload / Replace
          </button>
        </div>
      </div>

      {/* ============================
          CARD IMAGES
      ============================ */}
      <h4 className="mb-3">Card Images</h4>

      <div className="row">
        {form.Cards.map((c, idx) => (
          <div key={c.CardId} className="col-md-6 mb-4">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h6>Card {idx + 1}</h6>

                <img
                  src={resolveUrl(images.cards[c.CardId])}
                  className="rounded w-100 mb-2"
                  style={{ maxHeight: "180px", objectFit: "cover" }}
                  alt=""
                />

                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setUploadField(c.CardId);
                    setUploadFile(null);
                    setShowModal(true);
                  }}
                >
                  Upload / Replace
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ============================
          MODAL
      ============================ */}
      {showModal && (
        <>
          <div className="modal fade show" style={{ display: "block" }}>
            <div className="modal-dialog">
              <div className="modal-content shadow">
                <div className="modal-header">
                  <h5 className="modal-title">Upload Image</h5>
                  <button
                    className="btn-close"
                    onClick={() => setShowModal(false)}
                  />
                </div>

                <div className="modal-body">
                  <input
                    type="file"
                    accept="image/*"
                    className="form-control"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                  />
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>

                  <button className="btn btn-primary" onClick={handleUpload}>
                    Upload
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

export default VDModsEditor;
